import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { DeckVisibilityEnum } from "@sorcery/db";
import { Button, Checkbox, Input, Line, ModalBody, ModalHeader, P, toast } from "@sorcery/ui";

import { useModal } from "~/providers/ModalProvider";
import { avatarJotai, deckJotai, decklistJotai, sideboardJotai } from "~/state/deckJotai";
import { deckPath } from "~/utils/paths";
import { api } from "~/utils/trpc";

export const DeckCopyModal = () => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const avatar = useAtomValue(avatarJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const sideboard = useAtomValue(sideboardJotai)!;
  const { closeModal } = useModal();
  const { push } = useRouter();
  const trpcUtils = api.useUtils();

  const { mutateAsync: createDeck, error: trpcError } = api.deck.create.useMutation({
    onSuccess: (id) => {
      toast("Deck copied");
      void trpcUtils.deck.getLatest.invalidate();
      void trpcUtils.deck.getMyLatest.invalidate();
      void push(deckPath(id));
      closeModal();
    },
  });
  const [deckName, setDeckName] = useState("");
  const [visibility, setVisibility] = useState<DeckVisibilityEnum>(DeckVisibilityEnum.Private);
  const [nameMissingError, setNameMissingError] = useState(false);
  const [error, setError] = useState("");

  // Track trpc errors for display
  useEffect(() => {
    if (trpcError) {
      if (trpcError.shape?.data.code === "BAD_REQUEST") {
        setError(trpcError.shape?.message ?? "Something went wrong.");
      } else {
        // don't expose server side error messages
        setError("Something went wrong.");
      }
    }
  }, [trpcError]);

  const onSubmit = () => {
    // clear errs
    setError("");
    setNameMissingError(false);

    if (!deckName) return setNameMissingError(true);

    void createDeck({
      name: deckName,
      format: deck.format,
      visibility,
      featureId: deck.feature.id,
      avatars: [avatar],
      elements: deck.elements,
      decklist: decklist.map((dc) => ({
        cardId: dc.cardId,
        variantId: dc.variantId,
        quantity: dc.quantity,
      })),
      sideboard: sideboard.map((dc) => ({
        cardId: dc.cardId,
        variantId: dc.variantId,
        quantity: dc.quantity,
      })),
    });
  };

  return (
    <div className="p-6">
      <ModalHeader title="Copy Deck" />

      <ModalBody>
        {/* item */}
        <div className="mb-2 w-full">
          <div className="mb-1">Name</div>
          <div>
            <Input
              onChange={setDeckName}
              error={nameMissingError}
              placeholder="Name your copied deck..."
            />
          </div>
        </div>
        {/* item */}
        <div className="mb-4 w-full">
          <div className="mb-1">Visibility</div>
          <div className="flex space-x-2">
            {Object.values(DeckVisibilityEnum).map((v) => (
              <div
                className="items-top flex space-x-2"
                key={v}
                onClick={(newV) => newV && setVisibility(v)}
              >
                <Checkbox id="DeckVisibilityEnum" checked={visibility === v} label={v} />
              </div>
            ))}
          </div>
        </div>

        {/* description */}
        <div className="max-w-sm">
          <P className="mt-4">
            Private decks won't appear on our homepage, in public deck searches, or on your public
            profile. You can still share private decks to your friends by sending them a link, and
            import private decks to TTS.
          </P>

          <P className="mt-4">Dont worry, this can all be changed later.</P>
        </div>

        {/* error */}
        {error && (
          <div className="text-red my-4" role="alert">
            <p>
              <span className="font-bold">Error. </span>
              {error}
              <span>.</span>
            </p>
          </div>
        )}
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button onClick={onSubmit}>Copy</Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
